import React, { useState, useEffect } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import { staffSchedulesAPI, staffAPI } from '../services/api';
import { todayLocal, localDateKey } from '../utils/localDate';

function daysFromTodayLocal(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

// Safe helpers so bad data doesn't crash rendering
const safeNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const safeFixed = (v, digits = 1) => safeNumber(v, 0).toFixed(digits);
const safeTime = (t) => (typeof t === 'string' && t.length >= 5 ? t.substring(0, 5) : '—');
const safeDateLabel = (d) => {
  try {
    if (!d) return '—';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  } catch {
    return '—';
  }
};

const Schedules = ({ selectedBusiness }) => {
  const [schedules, setSchedules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [filters, setFilters] = useState({
    start_date: todayLocal(),
    end_date: daysFromTodayLocal(14)
  });
  const [formData, setFormData] = useState({
    staff_id: '',
    schedule_date: todayLocal(),
    start_time: '09:00',
    end_time: '17:00',
    break_duration_minutes: 60,
    shift_type: 'regular',
    schedule_status: 'scheduled',
    location: '',
    day_of_week: '',
    total_hours: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedBusiness, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        ...(selectedBusiness && { business_id: selectedBusiness }),
        ...filters
      };
      
      const [schedulesRes, statsRes, staffRes] = await Promise.all([
        staffSchedulesAPI.getAll(params),
        staffSchedulesAPI.getSummary(params),
        staffAPI.getAll(selectedBusiness ? { business_id: selectedBusiness, employment_status: 'active' } : { employment_status: 'active' })
      ]);
      
      setSchedules(schedulesRes.data);
      setStats(statsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error('Failed to load schedules:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        business_id: selectedBusiness || null
      };

      if (editingSchedule) {
        await staffSchedulesAPI.update(editingSchedule.schedule_id, dataToSubmit);
      } else {
        await staffSchedulesAPI.create(dataToSubmit);
      }
      
      setShowModal(false);
      setEditingSchedule(null);
      setFormData({
        staff_id: '',
        schedule_date: todayLocal(),
        start_time: '09:00',
        end_time: '17:00',
        break_duration_minutes: 60,
        shift_type: 'regular',
        schedule_status: 'scheduled',
        location: '',
        day_of_week: '',
        total_hours: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Failed to save schedule:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to save schedule');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      staff_id: schedule.staff_id,
      schedule_date: schedule.schedule_date?.split('T')[0],
      start_time: schedule.start_time?.substring(0, 5) || '09:00',
      end_time: schedule.end_time?.substring(0, 5) || '17:00',
      break_duration_minutes: schedule.break_duration_minutes || 60,
      shift_type: schedule.shift_type || 'regular',
      schedule_status: schedule.schedule_status || 'scheduled',
      location: schedule.location || '',
      day_of_week: schedule.day_of_week || '',
      total_hours: schedule.total_hours || '',
      notes: schedule.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await staffSchedulesAPI.delete(scheduleId);
      loadData();
    } catch (error) {
      console.error('Failed to delete schedule:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete schedule');
    }
  };

  if (loading) {
    return <div className="p-6">Loading schedules...</div>;
  }

  // Group schedules by date
  const schedulesByDate = schedules.reduce((acc, schedule) => {
    const date = schedule.schedule_date?.split('T')[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(schedule);
    return acc;
  }, {});

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Staff Schedules</h1>
            <p className="text-gray-600 mt-1">Manage employee work schedules and shifts</p>
          </div>
          <button
            onClick={() => {
              setEditingSchedule(null);
              setFormData({
                staff_id: '',
                schedule_date: todayLocal(),
                start_time: '09:00',
                end_time: '17:00',
                break_duration_minutes: 60,
                shift_type: 'regular',
                schedule_status: 'scheduled',
                location: '',
                day_of_week: '',
                total_hours: '',
                notes: ''
              });
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            + Add Schedule
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <ErrorBoundary>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Total Shifts</div>
                <div className="text-2xl font-bold text-gray-800">{safeNumber(stats.total_shifts)}</div>
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Total Hours</div>
                <div className="text-2xl font-bold text-blue-600">{safeFixed(stats.total_hours, 1)}</div>
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">Completed Shifts</div>
                <div className="text-2xl font-bold text-green-600">{safeNumber(stats.completed_shifts)}</div>
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="text-sm text-gray-600">No Shows</div>
                <div className="text-2xl font-bold text-red-600">{safeNumber(stats.no_show_shifts)}</div>
              </div>
            </ErrorBoundary>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Calendar View */}
      <div className="space-y-6">
        {Object.keys(schedulesByDate).length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No schedules found for the selected date range.
          </div>
        ) : (
          Object.entries(schedulesByDate).sort().map(([date, daySchedules]) => (
            <div key={date} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-100 px-6 py-3 border-b">
                <ErrorBoundary>
                  <h3 className="text-lg font-semibold text-gray-800">{safeDateLabel(date)}</h3>
                </ErrorBoundary>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {daySchedules.map((schedule) => (
                    <div 
                      key={schedule.schedule_id} 
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <ErrorBoundary>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{schedule.first_name} {schedule.last_name}</div>
                          <div className="text-sm text-gray-500">{schedule.job_title}</div>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className="flex-1 text-center">
                          <div className="text-sm font-medium text-gray-900">{safeTime(schedule.start_time)} - {safeTime(schedule.end_time)}</div>
                          <div className="text-xs text-gray-500">{safeFixed(schedule.total_hours, 1)} hours</div>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className="flex-1 text-center">
                          <span className={`text-xs px-3 py-1 rounded-full ${
                            schedule.shift_type === 'regular' ? 'bg-blue-100 text-blue-800' :
                            schedule.shift_type === 'overtime' ? 'bg-orange-100 text-orange-800' :
                            schedule.shift_type === 'holiday' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {String(schedule.shift_type || '').replace('_', ' ').toUpperCase() || '—'}
                          </span>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className="flex-1 text-center">
                          <span className={`text-xs px-3 py-1 rounded-full ${
                            schedule.schedule_status === 'completed' ? 'bg-green-100 text-green-800' :
                            schedule.schedule_status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                            schedule.schedule_status === 'called_off' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {String(schedule.schedule_status || '').replace('_', ' ').toUpperCase() || '—'}
                          </span>
                        </div>
                      </ErrorBoundary>
                      <ErrorBoundary>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(schedule)}
                            className="text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-600 rounded"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(schedule.schedule_id)}
                            className="text-red-600 hover:text-red-800 px-3 py-1 border border-red-600 rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </ErrorBoundary>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Member *
                  </label>
                  <select
                    value={formData.staff_id}
                    onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  >
                    <option value="">Select Staff</option>
                    {staff.map((s) => (
                      <option key={s.staff_id} value={s.staff_id}>
                        {s.first_name} {s.last_name} - {s.job_title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.schedule_date}
                    onChange={(e) => setFormData({ ...formData, schedule_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shift Type
                  </label>
                  <select
                    value={formData.shift_type}
                    onChange={(e) => setFormData({ ...formData, shift_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="regular">Regular</option>
                    <option value="overtime">Overtime</option>
                    <option value="holiday">Holiday</option>
                    <option value="on_call">On Call</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time *
                  </label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Break Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.break_duration_minutes}
                    onChange={(e) => setFormData({ ...formData, break_duration_minutes: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.schedule_status}
                    onChange={(e) => setFormData({ ...formData, schedule_status: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="called_off">Called Off</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSchedule(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingSchedule ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedules;
